import { createSlice } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import { authFailCheck } from "./auth.slice";
import { fetchInstructors } from "./instructors.slice";
import { notifyError, notifySuccess } from "./notification.slice";
import GroupService from "../services/GroupService";
import ContractService from "services/ContractService";

const initialState = {
  isLoading: false,
  selectedGroupId: 0,
  selectedContractId: 0,
  ids: [],
  entities: [],
  contracts: [],
  allContractIds: [],
  payments: [],
  discounts: [],
  history: [],
  groupSummary: {},
  filter: {
    status: 0,
    paymentStatus: 0,
    categoryId: 0,
    keyword: "",
    showArchived: false,
    sortBy: null,
    sortOrder: 1,
    pageNumber: 1,
    pageSize: 25,
  },
  contractFilter: {
    status: 0,
    groupStatus: 0,
    paymentStatus: 0,
    categoryId: 0,
    keyword: "",
    source: "",
    sortBy: null,
    sortOrder: 1,
    pageNumber: 1,
    pageSize: 25,
  },
};

const groupSlice = createSlice({
  name: "groups",
  initialState: initialState,
  reducers: {
    fetchRequest(state, action) {
      state.isLoading = true;
      state.filter = { ...state.filter, ...action.payload };
    },
    fetchFail(state, action) {
      state.isLoading = false;
    },
    fetchGroupDetailsRequest(state, action) {
      const { payload } = action;
      state.isLoading = true;
      state.selectedGroupId = payload;
    },
    fetchGroupsSuccess(state, action) {
      const { payload } = action;
      state.isLoading = false;
      state.pageCount = payload.pageCount;
      state.recordCount = payload.recordCount;
      state.ids = [];
      state.entities = [];
      payload.items.forEach((item, index) => {
        state.ids[index] = item.id;
        state.entities[item.id] = { ...state.entities[item.id], ...item };
      });
    },
    fetchGroupsSummarySuccess(state, action) {
      state.summary = action.payload;
    },
    fetchGroupDetailsRequestSuccess(state, action) {
      const { group, contracts } = action.payload;
      state.isLoading = false;
      state.entities[group.id] = { ...state.entities[group.id], ...group };

      state.entities[group.id].contractIds = [];

      contracts.forEach((contract) => {
        state.entities[group.id].contractIds.push(contract.id);
        state.contracts[contract.id] = contract;
      });
    },
    createContractSuccess(state, action) {
      const { contract } = action.payload;
      if (!state.entities[contract.groupId]) {
        state.ids.push(contract.groupId);
        state.entities[contract.groupId] = {};
        state.entities[contract.groupId].contractIds = [];
      }
      state.allContractIds.push(contract.id);
      state.contracts[contract.id] = contract;
      state.contracts[contract.id].paymentIds = [];
      state.contracts[contract.id].historyIds = [];
    },
    fetchContractDetailsRequest(state, action) {
      const { payload } = action;
      state.isLoading = true;
      state.selectedContractId = payload;
    },
    fetchContractDetailsSuccess(state, action) {
      const { contract, payments, discounts } = action.payload;
      state.isLoading = false;
      state.contracts[contract.id] = {
        ...state.contracts[contract.id],
        ...contract,
      };

      state.contracts[contract.id].paymentIds = [];
      state.contracts[contract.id].discountIds = [];
      state.contracts[contract.id].historyIds = [];

      payments.forEach((payment) => {
        state.contracts[contract.id].paymentIds.push(payment.id);
        state.payments[payment.id] = payment;
      });

      discounts.forEach((discount) => {
        state.contracts[contract.id].discountIds.push(discount.id);
        state.discounts[discount.id] = discount;
      });
    },
    changeGroupStatusSuccess(state, action) {
      const { payload } = action;
      state.isLoading = false;
      state.entities[payload.id] = payload;
    },
    changeContractStatusSuccess(state, action) {
      const { payload } = action;
      state.isLoading = false;
      state.contracts[payload.id] = payload;
    },
    deletePaymentSuccess(state, action) {
      const { contractId, paymentId, amount } = action.payload;
      state.isLoading = false;
      state.contracts[contractId].paidAmount -= amount;
      state.contracts[contractId].balance += amount;
      state.contracts[contractId].paymentIds = state.contracts[contractId].paymentIds.filter(
        (item) => item !== paymentId
      );
      state.payments[paymentId] = null;
    },
    createPaymentSuccess(state, action) {
      const { payload } = action;
      state.isLoading = false;
      const contract = state.contracts[payload.contractId];
      contract.paymentIds.push(payload.id);
      contract.paidAmount += payload.amount;
      contract.balance = contract.amount - contract.paidAmount;
      state.payments[payload.id] = payload;
    },
    applyDiscountSuccess(state, action) {
      const { payload } = action;
      state.isLoading = false;
      const contract = state.contracts[payload.contractId];
      contract.discountIds.push(payload.id);
      contract.amount -= payload.amount;
      contract.balance = contract.amount - contract.paidAmount;
      state.discounts[payload.id] = payload;
    },
    deleteDiscountSuccess(state, action) {
      const { contractId, discountId, amount } = action.payload;
      state.isLoading = false;
      state.contracts[contractId].amount += amount;
      state.contracts[contractId].balance += amount;
      state.contracts[contractId].discountIds = state.contracts[contractId].paymentIds.filter(
        (item) => item !== discountId
      );
      state.discounts[discountId] = null;
    },
    updateClientDetailsSuccess(state, action) {
      const { payload } = action;
      state.isLoading = false;
      state.contracts[payload.id] = payload;
    },
    fetchGroupSummarySuccess(state, action) {
      const { payload } = action;
      state.isLoading = false;
      state.groupSummary = payload;
    },
    deleteContractSuccess(state, action) {
      const { contractId } = action.payload;
      state.isLoading = false;
      const contract = state.contracts[contractId];
      if (contract) {
        const group = state.entities[contract.groupId];
        if (group) {
          if (group.contractIds) {
            group.contractIds = group.contractIds.filter((item) => item !== contractId);
          } else {
            group.contractIds = [];
          }
          group.totalAmount -= contract.amount;
          group.totalPaidAmount -= contract.paidAmount;
          group.totalBalance = group.totalAmount - group.totalPaidAmount;
        }
        state.allContractIds = state.allContractIds.filter((x) => x !== contractId);
      }
      state.selectedContractId = 0;
      state.contracts[contractId] = null;
    },
    fetchContractsRequest(state, action) {
      state.isLoading = true;
      state.contractFilter = { ...state.contractFilter, ...action.payload };
    },
    fetchContractsSuccess(state, action) {
      const { payload } = action;
      state.isLoading = false;
      state.contractsPageCount = payload.pageCount;
      state.contractsRecordCount = payload.recordCount;
      state.allContractIds = [];
      state.contracts = [];
      payload.items.forEach((contract) => {
        state.allContractIds.push(contract.id);
        state.contracts[contract.id] = contract;
      });
    },
    transferContractRequest(state, action) {
      state.isLoading = true;
    },
    transferContractSuccess(state, action) {
      state.isLoading = false;
      // Just mark the operation as successful without updating contract data
      // since the contract will be moved to a different branch
    },
  },
});

export default groupSlice.reducer;

const getGroupIds = ({ groups }) => groups.ids;
const getEntities = ({ groups }) => groups.entities;
export const getSelectedGroupId = ({ groups }) => groups.selectedGroupId;
const getAllContractIds = ({ groups }) => groups.allContractIds;
const getContracts = ({ groups }) => groups.contracts;
const getSelectedContractId = ({ groups }) => groups.selectedContractId;
const getPayments = ({ groups }) => groups.payments;
const getDiscounts = ({ groups }) => groups.discounts;

export const selectAllGroups = createSelector([getGroupIds, getEntities], (ids, entities) =>
  ids.map((id) => entities[id])
);

export const selectCurrentGroup = createSelector(
  [getSelectedGroupId, getEntities],
  (groupId, groups) => groups[groupId]
);

export const selectContractsOfCurrentGroup = createSelector(
  [getSelectedGroupId, getEntities, getContracts],
  (groupId, groups, contracts) => {
    const group = groups[groupId];
    if (!group || !group.contractIds) return [];
    return group.contractIds.map((contractId) => contracts[contractId]);
  }
);

export const selectCurrentContract = createSelector(
  [getSelectedContractId, getContracts],
  (contractId, contracts) => contracts[contractId]
);

export const selectPaymentsOfCurrentContract = createSelector(
  [getSelectedContractId, getContracts, getPayments],
  (contractId, contracts, payments) => {
    const contract = contracts[contractId];
    if (!contract || !contract.paymentIds) return [];
    return contract.paymentIds.map((paymentId) => payments[paymentId]);
  }
);

export const selectDiscountsOfCurrentContract = createSelector(
  [getSelectedContractId, getContracts, getDiscounts],
  (contractId, contracts, discounts) => {
    const contract = contracts[contractId];
    if (!contract || !contract.discountIds) return [];
    return contract.discountIds.map((discountId) => discounts[discountId]);
  }
);

export const selectAllContracts = createSelector(
  [getAllContractIds, getContracts],
  (ids, contracts) => ids.map((id) => contracts[id])
);

export const {
  fetchRequest,
  fetchFail,
  fetchGroupsSuccess,
  fetchGroupDetailsRequest,
  fetchGroupDetailsRequestSuccess,
  createContractSuccess,
  fetchContractDetailsSuccess,
  fetchContractDetailsRequest,
  changeGroupStatusSuccess,
  changeContractStatusSuccess,
  deletePaymentSuccess,
  createPaymentSuccess,
  updateClientDetailsSuccess,
  fetchGroupSummarySuccess,
  deleteContractSuccess,
  fetchContractsRequest,
  fetchContractsSuccess,
  transferContractSuccess,
  transferContractRequest,
  deleteDiscountSuccess,
  createDiscountSucces,
  applyDiscountSuccess,
} = groupSlice.actions;

export const fetchGroups = (filter) => async (dispatch) => {
  dispatch(fetchRequest(filter));
  const response = await GroupService.get(filter);
  if (response.ok) {
    dispatch(fetchGroupsSuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const fetchGroupSummary = () => async (dispatch) => {
  dispatch(fetchRequest());
  const response = await GroupService.getSummary();
  if (response.ok) {
    dispatch(fetchGroupSummarySuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const fetchGroupDetails = (groupId) => async (dispatch) => {
  dispatch(fetchGroupDetailsRequest(groupId));
  dispatch(fetchInstructors());
  const response = await GroupService.getGroupDetails(groupId);
  if (response.ok) {
    dispatch(fetchGroupDetailsRequestSuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const createContract = (contract, callbackFunc) => async (dispatch) => {
  dispatch(fetchRequest());
  const response = await ContractService.create(contract);
  if (response.ok) {
    dispatch(createContractSuccess({ contract: response.data }));
    if (callbackFunc) {
      callbackFunc(response.data.id);
    }
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const fetchContractDetails = (contractId) => async (dispatch) => {
  dispatch(fetchContractDetailsRequest(contractId));

  const response = await ContractService.get(contractId);
  if (response.ok) {
    dispatch(fetchContractDetailsSuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const changeContractStatus = (contractId, status) => async (dispatch, getState) => {
  dispatch(fetchRequest(contractId));
  const response = await ContractService.changeStatus(contractId, status);

  if (response.ok) {
    const contracts = getContracts(getState());
    const contract = contracts[contractId];
    dispatch(changeContractStatusSuccess({ ...contract, status }));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const changeGroupStatus = (groupId, status, data) => async (dispatch, getState) => {
  dispatch(fetchRequest());
  const response = await GroupService.changeStatus(groupId, status, data || {});

  if (response.ok) {
    dispatch(fetchGroupDetailsRequestSuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const changeGroupSchedule = (groupId, scheduleId) => async (dispatch) => {
  const response = await GroupService.changeSchedule(groupId, scheduleId);

  if (response.ok) {
    dispatch(fetchGroupDetailsRequestSuccess(response.data));
    dispatch(notifySuccess("Guruh dars vaqti o'zgartirildi."));
    return true;
  }

  dispatch(authFailCheck(response));
  dispatch(fetchFail(response.data));
  dispatch(notifyError(response.data?.message || "Dars vaqtini o'zgartirishda xatolik."));
  return false;
};

export const createPayment = (contractId, values) => async (dispatch) => {
  dispatch(fetchRequest());
  const response = await ContractService.createPayment(contractId, values);

  if (response.ok) {
    dispatch(createPaymentSuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const applyDiscount = (contractId, values) => async (dispatch) => {
  dispatch(fetchRequest());
  const response = await ContractService.applyDiscount(contractId, values);

  if (response.ok) {
    dispatch(applyDiscountSuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const deletePayment = (contractId, paymentId, amount) => async (dispatch) => {
  dispatch(fetchRequest());
  const response = await ContractService.deletePayment(paymentId);

  if (response.ok) {
    dispatch(deletePaymentSuccess({ contractId, paymentId, amount }));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const deleteDiscount = (contractId, discountId, amount) => async (dispatch) => {
  dispatch(fetchRequest());
  const response = await ContractService.deleteDiscount(discountId);

  if (response.ok) {
    dispatch(deleteDiscountSuccess({ contractId, discountId, amount }));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const updateClientDetails = (contractId, values) => async (dispatch, getState) => {
  dispatch(fetchRequest());
  const response = await ContractService.updateClientDetails(contractId, values);

  if (response.ok) {
    const contracts = getContracts(getState());
    const contract = contracts[contractId];

    dispatch(updateClientDetailsSuccess({ ...contract, ...response.data }));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const deleteContract = (contractId, data, callbackFunc) => async (dispatch) => {
  dispatch(fetchRequest());
  const response = await ContractService.delete(contractId, data);
  if (response.ok) {
    if (callbackFunc) {
      callbackFunc();
    }
    dispatch(deleteContractSuccess({ contractId }));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const forgiveDebt = (contractId, data, callbackFunc) => async (dispatch) => {
  dispatch(fetchRequest());
  const response = await ContractService.forgiveDebt(contractId, data);
  if (response.ok) {
    if (callbackFunc) {
      callbackFunc();
    }
    dispatch(fetchContractDetailsSuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const suspendContract = (contractId, data, callbackFunc) => async (dispatch) => {
  dispatch(fetchRequest());
  const response = await ContractService.suspend(contractId, data);
  if (response.ok) {
    if (callbackFunc) {
      callbackFunc();
    }
    dispatch(fetchContractDetailsSuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const transferContract = (contractId, data, callbackFunc) => async (dispatch) => {
  dispatch(transferContractRequest());
  const response = await ContractService.transferContract(contractId, data);
  if (response.ok) {
    if (callbackFunc) {
      callbackFunc();
    }
    dispatch(transferContractSuccess());
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const changeCategory = (contractId, data, callbackFunc) => async (dispatch) => {
  dispatch(fetchRequest());
  const response = await ContractService.changeCategory(contractId, data);
  if (response.ok) {
    if (callbackFunc) {
      callbackFunc();
    }
    dispatch(fetchContractDetailsSuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};

export const fetchContracts = (filter) => async (dispatch) => {
  dispatch(fetchContractsRequest(filter));
  const response = await ContractService.getAll(filter);
  if (response.ok) {
    dispatch(fetchContractsSuccess(response.data));
  } else {
    dispatch(authFailCheck(response));
    dispatch(fetchFail(response.data));
  }
};
